const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]';

export const KEYBOARD_SHORTCUT_PREF_NAMESPACE = 'viewer.keyboard_shortcuts';
export const KEYBOARD_SHORTCUT_STORAGE_KEY = 'kairos.viewerKeyboardShortcuts';

export const DEFAULT_TOOL_SHORTCUTS = Object.freeze({
  scroll: 's',
  wl: 'w',
  measure: 'm',
  roi: 'r',
  arrow: 'a',
  text: 't',
  select: 'v',
  pen: 'p',
  rect: 'b',
  ellipse: 'e',
  freezeViewport: 'f',
});

export const TOOL_SHORTCUT_LABELS = Object.freeze({
  scroll: 'Scroll tool',
  wl: 'Window / Level tool',
  measure: 'Measurement ruler',
  roi: 'ROI circle',
  arrow: 'Arrow annotation',
  text: 'Text annotation',
  select: 'Select / edit annotations',
  pen: 'Freehand pen',
  rect: 'Rectangle annotation',
  ellipse: 'Ellipse annotation',
  freezeViewport: 'Freeze active viewport',
});

const RESERVED_KEYS = new Set(['?', '/', 'escape', 'delete']);
const MODIFIER_ORDER = ['shift', 'alt'];

export const normalizeShortcutKey = (value) => {
  const parts = String(value || '').trim().toLowerCase().split('+').map(part => part.trim()).filter(Boolean);
  if (!parts.length) return '';
  const key = parts[parts.length - 1];
  const modifiers = parts.slice(0, -1);
  if (!/^[a-z]$/.test(key) || RESERVED_KEYS.has(key)) return '';
  if (modifiers.some(modifier => !MODIFIER_ORDER.includes(modifier))) return '';
  if (new Set(modifiers).size !== modifiers.length) return '';
  const canonicalModifiers = MODIFIER_ORDER.filter(modifier => modifiers.includes(modifier));
  return [...canonicalModifiers, key].join('+');
};

export const shortcutFromKeyboardEvent = (event = {}) => {
  if (event.metaKey || event.ctrlKey || event.getModifierState?.('AltGraph')) return '';
  const key = String(event.key || '').toLowerCase();
  if (!/^[a-z]$/.test(key)) return '';
  return normalizeShortcutKey(`${event.shiftKey ? 'shift+' : ''}${event.altKey ? 'alt+' : ''}${key}`);
};

export const formatShortcutLabel = (value) => normalizeShortcutKey(value)
  .split('+')
  .filter(Boolean)
  .map(part => part === 'shift' ? 'Shift' : part === 'alt' ? 'Alt' : part.toUpperCase())
  .join('+');

export const normalizeKeyboardShortcutPreferences = (value = {}) => {
  const source = value && typeof value === 'object' ? value : {};
  const custom = source.toolShortcuts && typeof source.toolShortcuts === 'object'
    ? source.toolShortcuts
    : source;
  const requested = {};
  const requestedOwners = new Map();
  const conflicts = [];

  Object.keys(DEFAULT_TOOL_SHORTCUTS).forEach((tool) => {
    const key = normalizeShortcutKey(custom[tool]);
    if (!key) return;
    requested[tool] = key;
    requestedOwners.set(key, [...(requestedOwners.get(key) || []), tool]);
  });

  const toolShortcuts = { ...DEFAULT_TOOL_SHORTCUTS };
  Object.entries(requested).forEach(([tool, key]) => {
    const owners = requestedOwners.get(key) || [];
    if (owners.length > 1) {
      if (owners[0] !== tool) return;
      conflicts.push({ key, tools: owners });
      toolShortcuts[tool] = key;
      return;
    }
    const defaultOwner = Object.entries(DEFAULT_TOOL_SHORTCUTS).find(([otherTool, defaultKey]) => otherTool !== tool && defaultKey === key)?.[0];
    if (defaultOwner && !requested[defaultOwner]) {
      conflicts.push({ key, tools: [defaultOwner, tool] });
      return;
    }
    toolShortcuts[tool] = key;
  });

  return { toolShortcuts, conflicts };
};

export const loadKeyboardShortcutPreferences = () => {
  if (typeof window === 'undefined') return normalizeKeyboardShortcutPreferences();
  try {
    return normalizeKeyboardShortcutPreferences(JSON.parse(window.localStorage.getItem(KEYBOARD_SHORTCUT_STORAGE_KEY) || '{}'));
  } catch (e) {
    return normalizeKeyboardShortcutPreferences();
  }
};

export const saveKeyboardShortcutPreferences = (value) => {
  const normalized = normalizeKeyboardShortcutPreferences(value);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(KEYBOARD_SHORTCUT_STORAGE_KEY, JSON.stringify({ toolShortcuts: normalized.toolShortcuts }));
    } catch (e) {
      // Profile-backed save remains authoritative; local fallback may be unavailable.
    }
  }
  return normalized;
};

export const shouldIgnoreViewerShortcut = (event = {}) => {
  const target = event.target;
  if (!target || typeof target.closest !== 'function') return false;
  try {
    return Boolean(target.closest(EDITABLE_SELECTOR));
  } catch (e) {
    return true;
  }
};

export const resolveViewerShortcut = (event = {}, wlPresets = [], toolShortcuts = DEFAULT_TOOL_SHORTCUTS) => {
  if (shouldIgnoreViewerShortcut(event)) return null;
  if (event.metaKey || event.ctrlKey || event.getModifierState?.('AltGraph')) return null;

  if (!event.altKey && (event.key === '?' || (event.key === '/' && event.shiftKey))) {
    return { type: 'help' };
  }

  if (!event.altKey && !event.shiftKey) {
    const preset = wlPresets.find(p => String(p.key) === String(event.key));
    if (preset) return { type: 'windowLevel', wl: { ww: preset.ww, wc: preset.wc }, preset };
  }

  const chord = shortcutFromKeyboardEvent(event);
  if (!chord) return null;
  const tool = Object.entries(toolShortcuts || DEFAULT_TOOL_SHORTCUTS)
    .find(([, shortcutKey]) => normalizeShortcutKey(shortcutKey) === chord)?.[0];
  return tool ? { type: 'tool', tool } : null;
};

export const viewerShortcutLabel = 'Profile shortcuts + 0–7 W/L';
